import { useFlags } from 'launchdarkly-react-client-sdk';
import { useEffect, useMemo, useReducer, useRef } from 'react';
import { useFormContext, useWatch } from 'react-hook-form';
import { FileUpload } from '@components/FileUpload';
import { datadogRum } from '@datadog/browser-rum';
import {
  ApplicationDocumentType,
  ApplicationEntityType,
  useVerifyIncomeDocumentUploadMutation,
} from '@graphql/generated/graphql';
import { useApplicationData } from '@provider';
import { Accordion, AccordionItem } from '@spark-web/accordion';
import { Box } from '@spark-web/box';
import { Button } from '@spark-web/button';
import { Divider } from '@spark-web/divider';
import { Spinner } from '@spark-web/spinner';
import { Stack } from '@spark-web/stack';
import { Text } from '@spark-web/text';
import { useTheme } from '@spark-web/theme';
import { INCOME_VERIFICATION, getApplicantTypeAndTrackingIdFromUrl } from '@utils';
import GreenCheckIcon from '../../../assets/green-check.svg?react';
import { IncomeVerificationErrorPanel } from './IncomeVerificationErrorPanel';
import { INCOME_VERIFICATION_ERROR_CONTENT, IncomeVerificationErrorCode } from './IncomeVerificationErrors';
import { getPrimaryIncomeDocumentsDescription } from './helpers';

type UploadDocumentProps = {
  name: string;
  closeDialog: () => void;
  incomeType?: 'primary' | 'additional';
  onDialogBusyChange?: (busy: boolean) => void;
  onVerificationStatusChange?: (status: VerificationStatus) => void;
};

type VerificationDocument = { documentId: string; fileName: string };

type VerificationResult = {
  documents: Array<{ documentId: string; validation: string; reason?: string | null; reasonCode?: string | null }>;
};

export type VerificationStatus = 'idle' | 'verifying' | 'success' | 'failed';

type VerificationStateModel = {
  status: VerificationStatus;
  stepIndex: number;
  errorCode: IncomeVerificationErrorCode | null;
  failureCount: number;
  documents: VerificationDocument[];
  result: VerificationResult | null;
};

type VerificationAction =
  | { type: 'start'; documents: VerificationDocument[] }
  | { type: 'step'; index: number }
  | { type: 'success'; result: VerificationResult }
  | { type: 'failed'; errorCode: IncomeVerificationErrorCode; result?: VerificationResult }
  | { type: 'reset-to-idle' };

const verificationStepIntervalsMs = [10000, 20000];
const verificationStepDurationMs = verificationStepIntervalsMs[verificationStepIntervalsMs.length - 1];

const initialVerificationState: VerificationStateModel = {
  status: 'idle',
  stepIndex: 0,
  errorCode: null,
  failureCount: 0,
  documents: [],
  result: null,
};

function verificationReducer(state: VerificationStateModel, action: VerificationAction): VerificationStateModel {
  switch (action.type) {
    case 'start':
      return {
        ...state,
        status: 'verifying',
        stepIndex: 0,
        errorCode: null,
        documents: action.documents,
        result: null,
      };
    case 'step':
      return { ...state, stepIndex: action.index };
    case 'success':
      return {
        ...state,
        status: 'success',
        result: action.result,
        failureCount: 0,
      };
    case 'failed':
      return {
        ...state,
        status: 'failed',
        errorCode: action.errorCode,
        result: action.result ?? state.result,
        failureCount: state.failureCount + 1,
      };
    case 'reset-to-idle':
      return { ...initialVerificationState, failureCount: state.failureCount };
    default:
      return state;
  }
}

function UploadDocuments({
  name,
  closeDialog,
  incomeType,
  onDialogBusyChange,
  onVerificationStatusChange,
}: UploadDocumentProps) {
  const theme = useTheme();
  const [verificationState, dispatchVerification] = useReducer(verificationReducer, initialVerificationState);
  const lastSuccessfulDocumentIdsRef = useRef<string[]>([]);
  const inFlightVerificationKeyRef = useRef<string | null>(null);
  const closeDialogRef = useRef(closeDialog);
  closeDialogRef.current = closeDialog;
  const existingDocumentsRef = useRef<VerificationDocument[]>([]);
  const { UPLOAD, UPLOAD_ADDITIONAL_INCOME_DOCUMENTS_DESCRIPTION } = INCOME_VERIFICATION;
  const { control, setValue } = useFormContext();
  const applicationId = useApplicationData('applicationId');
  const existingUploadedDocuments = useWatch({ control, name }) ?? [];
  const employmentType = useWatch({ control, name: 'applicant.employment.type' });
  // CT-3122: primary income upload copy is tailored to the applicant's declared employment type;
  // additional income keeps its generic list.
  const documentsDescription =
    incomeType === 'primary'
      ? getPrimaryIncomeDocumentsDescription(employmentType)
      : UPLOAD_ADDITIONAL_INCOME_DOCUMENTS_DESCRIPTION();
  existingDocumentsRef.current = (existingUploadedDocuments ?? []) as VerificationDocument[];
  const { applicantType } = getApplicantTypeAndTrackingIdFromUrl();
  const { mutateAsync: verifyIncomeDocumentUpload } = useVerifyIncomeDocumentUploadMutation({
    useErrorBoundary: true,
  });
  const flags = useFlags();
  const isIncomeVerificationFeedbackEnabled = flags['income-verification-feedback'] ?? false;
  const isPrimaryIncome = incomeType === 'primary';
  const shouldUseVerificationFeedback = isIncomeVerificationFeedbackEnabled && isPrimaryIncome;

  const verificationSteps = useMemo(
    () => [
      { label: 'Retrieving documents...', step: '1/3' },
      { label: 'Extracting information...', step: '2/3' },
      { label: 'Finishing up...', step: '3/3' },
    ],
    [],
  );
  const finalVerificationStepIndex = verificationSteps.length - 1;

  // Documents allowed per upload/verify (2) and cumulatively across retries (6). At 6 the
  // customer must remove a document before they can upload or verify more.
  const MAX_FILES = 6;
  const MAX_FILES_PER_UPLOAD = 2;
  // Verify attempts allowed before falling back to manual review. Set to 3 so the customer can use
  // all MAX_FILES / MAX_FILES_PER_UPLOAD = 3 upload batches (up to 6 documents) — a lower limit
  // would strand the cumulative cap short (e.g. 2 attempts caps the flow at 4 documents).
  const maxVerificationFailures = 3;
  const hasReachedRetryLimit =
    shouldUseVerificationFeedback && verificationState.failureCount >= maxVerificationFailures;
  const shouldShowVerificationFailure = shouldUseVerificationFeedback && verificationState.status === 'failed';
  const incomeVerificationSubmitLabel =
    shouldUseVerificationFeedback && shouldShowVerificationFailure ? 'Update documents' : 'Verify documents';

  const documentIdsKey = useMemo(
    () =>
      verificationState.documents
        .map(document => document.documentId)
        .sort((left, right) => left.localeCompare(right))
        .join('|'),
    [verificationState.documents],
  );

  useEffect(() => {
    if (!shouldUseVerificationFeedback || verificationState.status !== 'verifying') {
      return;
    }

    const stepTimeouts = verificationStepIntervalsMs.map((delayMs, index) =>
      setTimeout(() => {
        dispatchVerification({ type: 'step', index: index + 1 });
      }, delayMs),
    );

    return () => {
      stepTimeouts.forEach(timeoutId => clearTimeout(timeoutId));
    };
  }, [shouldUseVerificationFeedback, verificationState.status]);

  useEffect(() => {
    if (
      !shouldUseVerificationFeedback ||
      verificationState.status !== 'verifying' ||
      verificationState.documents.length === 0
    ) {
      return;
    }

    if (inFlightVerificationKeyRef.current === documentIdsKey) {
      return;
    }

    const currentDocumentIds = verificationState.documents
      .map(document => document.documentId)
      .sort((left, right) => left.localeCompare(right));
    const previousDocumentIds = [...lastSuccessfulDocumentIdsRef.current].sort((left, right) =>
      left.localeCompare(right),
    );
    const isSameDocumentSet =
      currentDocumentIds.length === previousDocumentIds.length &&
      currentDocumentIds.every((documentId, index) => documentId === previousDocumentIds[index]);

    if (isSameDocumentSet) {
      dispatchVerification({ type: 'reset-to-idle' });
      return;
    }

    let isCancelled = false;

    const waitForStepProgress = async (startTimeMs: number) => {
      if (!isPrimaryIncome) {
        return;
      }

      const elapsedMs = Date.now() - startTimeMs;
      const remainingMs = Math.max(verificationStepDurationMs - elapsedMs, 0);

      if (remainingMs > 0) {
        await new Promise(resolve => setTimeout(resolve, remainingMs));
      }
    };

    const ensureFinalStepVisible = async () => {
      if (!isPrimaryIncome) {
        return;
      }

      if (verificationState.stepIndex < finalVerificationStepIndex) {
        dispatchVerification({ type: 'step', index: finalVerificationStepIndex });
        await new Promise(resolve => setTimeout(resolve, 0));
      }
    };

    const verifyDocuments = async () => {
      const startTimeMs = Date.now();
      inFlightVerificationKeyRef.current = documentIdsKey;

      try {
        const existingDocIds = existingDocumentsRef.current.map(document => document.documentId);
        const newDocIds = verificationState.documents.map(document => document.documentId);
        const allDocumentIds = Array.from(new Set([...existingDocIds, ...newDocIds]));

        const response = await verifyIncomeDocumentUpload({
          VerifyIncomeDocumentUploadInput: {
            applicationId: applicationId ?? '',
            documentIds: allDocumentIds,
          },
        });

        await waitForStepProgress(startTimeMs);

        if (isCancelled) {
          return;
        }

        await ensureFinalStepVisible();

        const result = response.verifyIncomeDocumentUpload;
        const verificationResult = {
          documents: result.documents.map(document => ({
            documentId: document.documentId,
            validation: document.validation,
            reason: document.reason,
            reasonCode: document.failureCode,
          })),
        };

        if (result.validation === 'Failed') {
          const newFailureCount = verificationState.failureCount + 1;
          if (newFailureCount === 1) {
            datadogRum.addAction('Income document verification failed first attempt', {
              statusCode: result.statusCode,
              failureCount: newFailureCount,
            });
          } else if (newFailureCount === 2) {
            datadogRum.addAction('Income document verification failed second attempt', {
              statusCode: result.statusCode,
              failureCount: newFailureCount,
            });
          } else if (newFailureCount === maxVerificationFailures) {
            // Final attempt — the customer is now dropped to manual review.
            datadogRum.addAction('Income document verification failed third attempt', {
              statusCode: result.statusCode,
              failureCount: newFailureCount,
            });
          }
          dispatchVerification({
            type: 'failed',
            errorCode: result.statusCode as IncomeVerificationErrorCode,
            result: verificationResult,
          });
          if (shouldUseVerificationFeedback) {
            setValue('incomeVerification', 'Failed');
          }
          return;
        }

        if (shouldUseVerificationFeedback) {
          setValue('incomeVerification', undefined);
        }

        dispatchVerification({ type: 'success', result: verificationResult });
      } catch {
        await waitForStepProgress(startTimeMs);

        if (isCancelled) {
          return;
        }

        await ensureFinalStepVisible();

        const newFailureCount = verificationState.failureCount + 1;
        if (newFailureCount === 1) {
          datadogRum.addAction('Income document verification failed first attempt', {
            statusCode: IncomeVerificationErrorCode.DOCUMENT_UNREADABLE,
            failureCount: newFailureCount,
          });
        } else if (newFailureCount === 2) {
          datadogRum.addAction('Income document verification failed second attempt', {
            statusCode: IncomeVerificationErrorCode.DOCUMENT_UNREADABLE,
            failureCount: newFailureCount,
          });
        } else if (newFailureCount === maxVerificationFailures) {
          // Final attempt — the customer is now dropped to manual review.
          datadogRum.addAction('Income document verification failed third attempt', {
            statusCode: IncomeVerificationErrorCode.DOCUMENT_UNREADABLE,
            failureCount: newFailureCount,
          });
        }
        dispatchVerification({
          type: 'failed',
          errorCode: IncomeVerificationErrorCode.DOCUMENT_UNREADABLE,
        });
        if (shouldUseVerificationFeedback) {
          setValue('incomeVerification', 'Failed');
        }
      }
    };

    verifyDocuments();

    return () => {
      isCancelled = true;
    };
  }, [
    applicationId,
    documentIdsKey,
    finalVerificationStepIndex,
    shouldUseVerificationFeedback,
    verificationState.status,
    verifyIncomeDocumentUpload,
  ]);

  useEffect(() => {
    if (verificationState.status !== 'verifying') {
      inFlightVerificationKeyRef.current = null;
    }
  }, [verificationState.status]);

  useEffect(() => {
    onDialogBusyChange?.(verificationState.status === 'verifying');
  }, [verificationState.status, onDialogBusyChange]);

  useEffect(() => {
    onVerificationStatusChange?.(verificationState.status);
  }, [verificationState.status, onVerificationStatusChange]);

  useEffect(() => {
    if (!shouldUseVerificationFeedback || verificationState.status !== 'success') {
      return;
    }

    lastSuccessfulDocumentIdsRef.current = verificationState.documents
      .map(document => document.documentId)
      .sort((left, right) => left.localeCompare(right));
    const existingDocs = existingDocumentsRef.current;
    const existingIds = new Set(existingDocs.map(document => document.documentId));
    const mergedDocuments = [
      ...existingDocs,
      ...verificationState.documents.filter(document => !existingIds.has(document.documentId)),
    ];
    setValue(name, mergedDocuments);

    const closeTimeout = setTimeout(() => {
      closeDialogRef.current();
    }, 5000);

    return () => clearTimeout(closeTimeout);
  }, [verificationState.status]);

  const shouldShowVerificationProgress =
    shouldUseVerificationFeedback &&
    (verificationState.status === 'verifying' || verificationState.status === 'success');

  if (shouldShowVerificationProgress) {
    let verificationContent: React.ReactNode;

    if (verificationState.status === 'success') {
      verificationContent = (
        <>
          <Box display="flex" alignItems="center" justifyContent="center">
            <GreenCheckIcon />
          </Box>
          <Stack gap="medium" align="center">
            <Text weight="semibold" size="large">
              Your income has been verified!
            </Text>
            <Text align="center" tone="muted" size="small">
              We&apos;ve successfully verified your income from the documents you uploaded. Your application will now
              continue.
            </Text>
          </Stack>
          <Button disabled>Submitting documents</Button>
        </>
      );
    } else {
      verificationContent = (
        <>
          <Box display="flex" alignItems="center" justifyContent="center">
            <Spinner tone="positive" />
          </Box>
          <Stack gap="xxlarge" align="center">
            <Text weight="semibold" size="large" align="center">
              {verificationSteps[verificationState.stepIndex]?.label} (
              {verificationSteps[verificationState.stepIndex]?.step})
            </Text>
            <Text align="center" tone="muted" size="small">
              Document verification typically takes 1-3 minutes.
              <br />
              Do not close this window.
            </Text>
          </Stack>
        </>
      );
    }

    return (
      <Box display="flex" alignItems="center" justifyContent="center" paddingY="xxlarge" css={{ minHeight: '320px' }}>
        <Stack gap="xxlarge" align="center">
          {verificationContent}
        </Stack>
      </Box>
    );
  }

  const resolvedErrorCode =
    verificationState.errorCode && INCOME_VERIFICATION_ERROR_CONTENT[verificationState.errorCode]
      ? verificationState.errorCode
      : IncomeVerificationErrorCode.DOCUMENT_UNREADABLE;
  const resolvedErrorContent = INCOME_VERIFICATION_ERROR_CONTENT[resolvedErrorCode];
  const isMultipleDocumentFailure = resolvedErrorCode === IncomeVerificationErrorCode.MULTIPLE_DOCUMENT_TYPE_FAILED;
  const failedReasonCodes =
    verificationState.result?.documents
      .filter(document => document.validation !== 'Passed')
      .map(document => document.reasonCode ?? document.reason)
      .filter((reason): reason is IncomeVerificationErrorCode =>
        Boolean(reason && reason in INCOME_VERIFICATION_ERROR_CONTENT),
      ) ?? [];
  const failedReasonContents = Array.from(
    new Set(failedReasonCodes.map(reason => INCOME_VERIFICATION_ERROR_CONTENT[reason])),
  );
  const multipleDescriptions = Array.from(
    new Set(
      failedReasonContents
        .map(content => content.description)
        .filter((description): description is string => Boolean(description)),
    ),
  );
  const errorPanelProps = isMultipleDocumentFailure
    ? {
        title: resolvedErrorContent.title,
        descriptionItems: multipleDescriptions,
      }
    : {
        title: resolvedErrorContent.title,
        description: resolvedErrorContent.description,
        callout: resolvedErrorContent.callout,
        guidanceTitle: resolvedErrorContent.guidanceTitle,
        guidanceItems: resolvedErrorContent.guidanceItems,
      };
  // documentIds that failed verification — used to flag the offending file rows under the dropzone.
  const failedDocumentIds =
    verificationState.result?.documents
      .filter(document => document.validation !== 'Passed')
      .map(document => document.documentId) ?? [];

  const legacyContent = (
    <Stack gap="xxlarge">
      <>
        {documentsDescription}

        <FileUpload
          title={UPLOAD}
          labelVisibility="hidden"
          fieldName={name}
          setFieldValue={setValue}
          applicationId={applicationId ?? ''}
          existingUploadedDocuments={existingUploadedDocuments}
          documentType={ApplicationDocumentType.IncomeVerification}
          applicantType={ApplicationEntityType[applicantType]}
          submitLabel={incomeVerificationSubmitLabel}
          maxFiles={MAX_FILES}
          maxFilesPerUpload={MAX_FILES_PER_UPLOAD}
          onVerificationStart={uploadedDocuments => {
            if (shouldUseVerificationFeedback) {
              dispatchVerification({ type: 'start', documents: uploadedDocuments });
              return;
            }

            if (!isPrimaryIncome) {
              closeDialog();
              return;
            }

            const existingDocIds = existingDocumentsRef.current.map(document => document.documentId);
            const newDocIds = uploadedDocuments.map(document => document.documentId);
            const allDocumentIds = Array.from(new Set([...existingDocIds, ...newDocIds]));
            verifyIncomeDocumentUpload({
              VerifyIncomeDocumentUploadInput: {
                applicationId: applicationId ?? '',
                documentIds: allDocumentIds,
              },
            });

            closeDialog();
          }}
        />
      </>
    </Stack>
  );

  return shouldUseVerificationFeedback ? (
    <Stack gap="xxlarge" align="stretch">
      {shouldShowVerificationFailure ? (
        // Caution stroke (#be5c1c) + padding are desktop-only; on mobile the panel goes edge-to-edge.
        // Driven via a media query because Box's responsive `border` prop doesn't apply reliably.
        <Box
          borderRadius="medium"
          css={{
            [`@media (min-width: ${theme.breakpoint.tablet}px)`]: {
              padding: theme.spacing.medium,
              border: `${theme.border.width.standard} solid ${theme.border.color.caution}`,
            },
          }}
        >
          <Stack gap="large" align="stretch">
            <IncomeVerificationErrorPanel {...errorPanelProps} />
            <FileUpload
              title={UPLOAD}
              labelVisibility="hidden"
              fieldName={name}
              setFieldValue={setValue}
              applicationId={applicationId ?? ''}
              existingUploadedDocuments={existingUploadedDocuments}
              failedDocumentIds={failedDocumentIds}
              documentType={ApplicationDocumentType.IncomeVerification}
              applicantType={ApplicationEntityType[applicantType]}
              maxFiles={MAX_FILES}
              maxFilesPerUpload={MAX_FILES_PER_UPLOAD}
              onVerificationStart={uploadedDocuments => {
                dispatchVerification({ type: 'start', documents: uploadedDocuments });
              }}
              onVerificationError={() => dispatchVerification({ type: 'reset-to-idle' })}
              isVerificationFailed={shouldShowVerificationFailure}
              isUploadDisabled={hasReachedRetryLimit}
              submitLabel={
                hasReachedRetryLimit && shouldUseVerificationFeedback
                  ? 'Continue and verify manually'
                  : incomeVerificationSubmitLabel
              }
              allowSubmitWhenNoSelection={hasReachedRetryLimit && shouldUseVerificationFeedback}
              onManualContinue={
                hasReachedRetryLimit && shouldUseVerificationFeedback
                  ? () => {
                      closeDialog();
                    }
                  : undefined
              }
            />
            <Divider />
            {/* AccordionItem's own padding is all-sides; we move it to the wrapper so the horizontal
                padding can shrink to 4px on mobile while vertical stays 16px. The `label` is a plain
                string rendered inside a Spark Heading/Text, so the trigger label size (15px mobile /
                17px desktop) is set via a scoped override. */}
            <Box
              paddingY="large"
              paddingX={{ mobile: 'xsmall', tablet: 'large' }}
              css={{
                [`@media (max-width: ${theme.breakpoint.tablet - 1}px)`]: {
                  'button span': { fontSize: `${theme.typography.text.small.mobile.fontSize}px` },
                },
              }}
            >
              <Accordion type="single" collapsible>
                <AccordionItem label="What documents can I upload?" value="what-documents" padding="none">
                  {documentsDescription}
                </AccordionItem>
              </Accordion>
            </Box>
          </Stack>
        </Box>
      ) : null}
      {!shouldShowVerificationFailure ? (
        <>
          <Box>{documentsDescription}</Box>
          <FileUpload
            title={UPLOAD}
            labelVisibility="hidden"
            fieldName={name}
            setFieldValue={setValue}
            applicationId={applicationId ?? ''}
            existingUploadedDocuments={existingUploadedDocuments}
            documentType={ApplicationDocumentType.IncomeVerification}
            applicantType={ApplicationEntityType[applicantType]}
            maxFiles={MAX_FILES}
            maxFilesPerUpload={MAX_FILES_PER_UPLOAD}
            onVerificationStart={uploadedDocuments => {
              dispatchVerification({ type: 'start', documents: uploadedDocuments });
            }}
            onVerificationError={() => dispatchVerification({ type: 'reset-to-idle' })}
            submitLabel={incomeVerificationSubmitLabel}
          />
        </>
      ) : null}
    </Stack>
  ) : (
    legacyContent
  );
}

export default UploadDocuments;
